
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/ui/logo";
import { useLogin } from "@/hooks/useLogin";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface LoginFormProps {
  redirectParams?: URLSearchParams | null;
  from?: string;
}

export const LoginForm = ({ redirectParams, from }: LoginFormProps) => {
  const [searchParams] = useSearchParams();
  const { formData, formError, isLoading, handleChange, handleSubmit, setFrom } = useLogin();
  const [signupLink, setSignupLink] = useState("/signup");
  
  // Set the 'from' state in the login hook
  useEffect(() => {
    if (from) {
      setFrom(from);
    }
  }, [from, setFrom]);
  
  // Use provided redirectParams or extract from current location
  const effectiveParams = redirectParams || searchParams;
  
  // Get parameters from both possible sources
  const urlParams = new URLSearchParams(window.location.search);
  const courseToEnroll = effectiveParams.get("course") || urlParams.get("course");
  const price = effectiveParams.get("price") || urlParams.get("price");
  const name = effectiveParams.get("name") || urlParams.get("name");
  
  // Check if there's persisted enrollment data in sessionStorage
  useEffect(() => {
    const pendingEnrollment = sessionStorage.getItem('pendingEnrollment');
    if (pendingEnrollment) {
      try {
        const enrollData = JSON.parse(pendingEnrollment);
        console.log("[LoginForm] Found persisted enrollment data:", enrollData);
        
        // If we don't have URL parameters but have stored data, use that
        if (!courseToEnroll && enrollData.courseId) {
          const newParams = new URLSearchParams(window.location.search);
          newParams.set('course', enrollData.courseId);
          newParams.set('enroll', 'true');
          
          if (enrollData.price) newParams.set('price', enrollData.price);
          if (enrollData.name) newParams.set('name', enrollData.name);
          
          // Update the URL with the stored parameters without reloading
          window.history.replaceState(
            {}, 
            '', 
            `${window.location.pathname}?${newParams.toString()}`
          );
          
          console.log("[LoginForm] Updated URL with stored enrollment data");
        }
      } catch (error) {
        console.error("[LoginForm] Error parsing stored enrollment data:", error);
      }
    }
  }, [courseToEnroll]);
  
  useEffect(() => {
    console.log("[LoginForm] Window URL:", window.location.href);
    console.log("[LoginForm] Initialized with params:", {
      hasCourse: !!courseToEnroll,
      courseId: courseToEnroll,
      price,
      name,
      from,
      timestamp: new Date().toISOString(),
      searchParamsEntries: Object.fromEntries(searchParams.entries()),
      urlParamsEntries: Object.fromEntries(urlParams.entries()),
      effectiveParamsEntries: effectiveParams ? Object.fromEntries(effectiveParams.entries()) : {}
    });

    // Build signup link with parameters and from state
    let newSignupLink = "/signup";
    
    // Always pass the from state to signup
    const navigateState = from ? { from } : {};
    
    if (courseToEnroll) {
      const params = new URLSearchParams();
      params.append('course', courseToEnroll);
      params.append('enroll', 'true');
      
      if (price) params.append('price', price);
      if (name) params.append('name', name);
      
      newSignupLink += `?${params.toString()}`;
      console.log("[LoginForm] Built signup link with course params:", newSignupLink);
    } else if (searchParams.toString()) {
      newSignupLink += `?${searchParams.toString()}`;
      console.log("[LoginForm] Built signup link with search params:", newSignupLink);
    } else if (urlParams.toString()) {
      newSignupLink += `?${urlParams.toString()}`;
      console.log("[LoginForm] Built signup link with URL params:", newSignupLink);
    }
    
    setSignupLink(newSignupLink);
  }, [courseToEnroll, price, name, searchParams, urlParams, effectiveParams, from]);

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto mb-2">
          <Logo size="lg" />
        </div>
        <CardTitle className="text-2xl font-heading">Welcome back</CardTitle>
        <CardDescription>
          Log in to your account to continue
          {courseToEnroll && (
            <p className="mt-2 text-sm">You'll be enrolled in the course after logging in</p>
          )}
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4 mr-2" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="your@email.com"
              required
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Your password"
              required
              value={formData.password}
              onChange={handleChange}
            />
          </div>
          
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Logging in..." : "Log in"}
          </Button>
        </form>
      </CardContent>
      
      <CardFooter>
        <p className="text-center text-sm text-muted-foreground w-full">
          Don't have an account?{" "}
          <Link to={signupLink} state={{ from }} className="text-primary font-medium hover:underline">
            Sign up
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
};
